import React, { useState, useEffect, Fragment } from 'react'
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import formatCPF from '@brazilian-utils/format-cpf'
import { format } from 'date-fns'
import { Col, Row, Table, Button, Spinner, Card } from 'react-bootstrap'

import RelatorioService from '../../services/relatorio.service'
import PageLoading from '../Common/PageLoading'
import Paginator from '../Common/Paginator'
import CSVDownload from './CSVDownload'
import { formatMoney } from '../../utils/string.utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'

const montarLinkPaciente = (pacienteId) => {
  if (!pacienteId) return '-'
  return `${window.location.origin}/pacientes/${pacienteId}`
}

const montarFiltros = (dateFilterDe, dateFilterAte, mostrarProtese) => [
  { campo: 'dataInicial', valor: dateFilterDe },
  { campo: 'dataFinal', valor: dateFilterAte },
  { campo: 'mostrarProtese', valor: mostrarProtese },
]

export default function VencimentoParcela({ selectedRelatorio }) {
  const itensPorPagina = 20

  const [isLoading, setLoading] = useState(false)
  const [isLoadingCSV, setLoadingCSV] = useState(false)
  const [itensLista, setItensLista] = useState([])
  const [cabecalhoRelatorio] = useState(['Nome', 'CPF', 'Telefone', 'Valor a Vencer', 'Vencimento', 'Link do Paciente'])
  const [dateFilterDe, setDateFilterDe] = useState(new Date().toISOString())
  const [dateFilterAte, setDateFilterAte] = useState(new Date().toISOString())
  const [feedbackBusca, setFeedbackBusca] = useState('')
  const [pagina, setPagina] = useState(0)
  const [totalPaginas, setTotalPaginas] = useState(0)
  const [mostrarProtese, setMostrarProtese] = useState(false)
  const [resumo, setResumo] = useState(null)

  useEffect(() => {
    handleSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilterDe, dateFilterAte, mostrarProtese, pagina])

  const relatorioItem = (item) => ({
    nomePaciente: item.nomePaciente ? item.nomePaciente.toUpperCase() : '-',
    cpf: item.cpf ? formatCPF(item.cpf) : '-',
    telefone: item.telefone || '-',
    valorAVencer: item.valorAVencer != null ? formatMoney(item.valorAVencer) : '-',
    dataVencimento: item.dataVencimento ? format(new Date(item.dataVencimento), 'dd/MM/yyyy') : '-',
    linkPaciente: montarLinkPaciente(item.pacienteId),
  })

  const formatarValorResumo = (descricao, valor) => {
    if (!valor) return descricao?.toLowerCase().includes('valor') ? formatMoney(0) : '0'
    return descricao?.toLowerCase().includes('valor') ? valor : valor
  }

  const getValuesLista = async () => {
    setLoadingCSV(true)
    try {
      const filtros = montarFiltros(dateFilterDe, dateFilterAte, mostrarProtese)
      const response = await RelatorioService.getRegistrosVencimentoParcela(filtros, 1, 999999)
      const dados = response?.lista?.dados ?? []

      return {
        data: dados.map((item) => Object.values(relatorioItem(item))),
        headers: cabecalhoRelatorio,
        filename: `${selectedRelatorio.label} - ${format(new Date(), 'dd-MM-yyyy')}.csv`,
      }
    } finally {
      setLoadingCSV(false)
    }
  }

  const onChangeMostrarProtese = (e) => {
    setMostrarProtese(e.target.checked)
    setPagina(0)
  }

  const handleSearch = async () => {
    setLoading(true)
    try {
      setFeedbackBusca('')
      const filtros = montarFiltros(dateFilterDe, dateFilterAte, mostrarProtese)
      const { lista, resumo } = await RelatorioService.getRegistrosVencimentoParcela(
        filtros,
        pagina + 1,
        itensPorPagina
      )
      setItensLista(lista?.dados ?? [])
      setTotalPaginas(lista?.totalPaginas ?? 0)
      setResumo(resumo)
      if ((lista?.dados ?? []).length === 0) {
        setFeedbackBusca('Nenhum registro encontrado')
      }
    } catch (error) {
      console.error(error)
    }
    setLoading(false)
  }

  const handleDateFilterDe = (date) => {
    if (date) {
      setDateFilterDe(date.toDate())
    } else {
      setDateFilterDe(null)
    }
    setPagina(0)
  }

  const handleDateFilterAte = (date) => {
    if (date) {
      setDateFilterAte(date.toDate())
    } else {
      setDateFilterAte(null)
    }
    setPagina(0)
  }

  const handleClear = () => {
    setDateFilterDe(new Date().toISOString())
    setDateFilterAte(new Date().toISOString())
    setMostrarProtese(false)
    setPagina(0)
  }

  const handlePagina = (data) => setPagina(data.selected)

  const renderItens = () =>
    itensLista.length > 0 ? (
      <Fragment>
        <Card className='mb-4 py-2 mt-4'>
          <Row>
            {resumo &&
              resumo.map(({ descricao, valor }, index) => (
                <Col key={index} className='d-flex align-items-center justify-content-center flex-column'>
                  <small className='text-center text-muted m-0'>{!!descricao ? descricao.toUpperCase() : '-'}</small>
                  <h4 className='text-center m-0 font-weight-bold'>{formatarValorResumo(descricao, valor)}</h4>
                </Col>
              ))}
          </Row>
        </Card>

        <Table hover size='sm' className='border rounded-0 shadow-sm' style={{ backgroundColor: 'white' }}>
          <thead>
            <tr className='text-center'>
              <th width='180px' className='text-left'>Nome</th>
              <th width='120px'>CPF</th>
              <th width='160px'>Telefone</th>
              <th width='110px'>Valor a Vencer</th>
              <th width='100px'>Vencimento</th>
              <th width='100px'>Detalhe</th>
            </tr>
          </thead>
          <tbody>
            {itensLista.map((item, index) => {
              const { pacienteId, nomePaciente, cpf, telefone, valorAVencer, dataVencimento } = item

              return (
                <tr key={index} className='text-center' style={{ fontSize: 'smaller' }}>
                  <td className='text-left'>{nomePaciente ? nomePaciente.toUpperCase() : '-'}</td>
                  <td>{cpf ? formatCPF(cpf) : '-'}</td>
                  <td>{telefone || '-'}</td>
                  <td>{valorAVencer != null ? formatMoney(valorAVencer) : '-'}</td>
                  <td>{dataVencimento ? format(new Date(dataVencimento), 'dd/MM/yyyy') : '-'}</td>
                  <td>
                    {pacienteId ? (
                      <a
                        href={`/pacientes/${pacienteId}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        style={{ color: '#007bff', textDecoration: 'none', cursor: 'pointer', fontWeight: '500' }}
                        onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
                        onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
                        title='Abrir cadastro do paciente em nova aba'
                      >
                        Ver paciente <FontAwesomeIcon icon={faExternalLinkAlt} size='xs' style={{ marginLeft: '5px', opacity: 0.7 }} />
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
        {totalPaginas > 1 && <Paginator handlePagina={handlePagina} pageCount={totalPaginas} paginaAtual={pagina} />}
      </Fragment>
    ) : (
      <div className='mt-4'>{feedbackBusca}</div>
    )

  const renderLoading = () => <PageLoading />

  return (
    <Fragment>
      <Row>
        <Col md='3' className='d-flex align-items-center'>
          <MuiPickersUtilsProvider utils={MomentUtils} locale='pt-br'>
            <KeyboardDatePicker
              invalidDateMessage='Formato de data inválido'
              value={dateFilterDe}
              onChange={handleDateFilterDe}
              className='form-control'
              variant='inline'
              format='DD/MM/YYYY'
              maxDate={dateFilterAte || new Date('2100-01-01')}
              animateYearScrolling
              autoOk
              placeholder='De'
            />
          </MuiPickersUtilsProvider>
        </Col>
        <Col md='3' className='d-flex align-items-center'>
          <MuiPickersUtilsProvider utils={MomentUtils} locale='pt-br'>
            <KeyboardDatePicker
              invalidDateMessage='Formato de data inválido'
              value={dateFilterAte}
              onChange={handleDateFilterAte}
              minDate={dateFilterDe || new Date('1900-01-01')}
              className='form-control mx-1'
              variant='inline'
              format='DD/MM/YYYY'
              animateYearScrolling
              autoOk
              placeholder='Até'
            />
          </MuiPickersUtilsProvider>
        </Col>
        <Col md='3' className='d-flex align-items-center'>
          <label className='checkbox m-0'>
            <input type='checkbox' checked={mostrarProtese} onChange={onChangeMostrarProtese} />
            <span className='d-flex align-items-center'>Mostrar parcelas de prótese</span>
          </label>
        </Col>
        <Col className='d-flex align-items-center'>
          <Button onClick={handleClear} variant='outline-danger' className='mr-2'>
            Limpar
          </Button>
          <Button onClick={handleSearch} variant='primary' className='mr-2'>
            Pesquisar
          </Button>
          <CSVDownload asyncExportMethod={getValuesLista}>
            <Button disabled={isLoadingCSV} variant='outline-secondary'>
              {isLoadingCSV ? <Spinner animation='border' size='sm' variant='light' /> : 'Gerar CSV'}
            </Button>
          </CSVDownload>
        </Col>
      </Row>
      {isLoading ? renderLoading() : renderItens()}
    </Fragment>
  )
}
